import { BarcodeType, DbRef } from '@caresend/types';
import { getSupplyItemPath } from '@caresend/utils';
import 'firebase/auth';
import '../../database/firebaseConfig/cloudFunctions.js';
import '../../database/firebaseConfig/init.js';
import { getValueOnce } from '../../database/firebaseConfig/methods.js';
import '@sentry/vue';
import { toastErrorAndReport, toastError } from '../toast/toast.js';
import '@fortawesome/fontawesome-svg-core';
import 'buefy';
import '../../components/ui/Icon.vue.js';
import '../configuration/icons.js';
import '../../database/algoliaConfig/indices.js';
import 'mobile-device-detect';
import '../tracking/index.js';
import { trackScanItem } from '../tracking/tracking.js';
import '@firebase/auth';
import 'browser-image-compression';
import 'firebase/storage';
import 'js-sha256';
import { SupplyItemType } from '../../store/modules/procedures/model.js';

SupplyItemType.AMBER_BAG;
SupplyItemType.CARESEND_BAG;
SupplyItemType.INSULATED_BAG;
SupplyItemType.PROCEDURE_BAG;
SupplyItemType.SPECIMEN_BAG;
const handleScanningErrorOrWarning = async (message, reportToSentry = false, barcode) => {
    if (reportToSentry)
        toastErrorAndReport(message);
    else
        toastError(message);
    await trackScanItem(barcode, message);
};
async function handleScanningError(message, item) {
    const barcodeToTrack = typeof item === 'string'
        ? { id: item, type: BarcodeType.SUPPLY_ITEM }
        : item;
    await handleScanningErrorOrWarning(message, true, barcodeToTrack);
}
async function handleScanningWarning(message, item) {
    const barcodeToTrack = typeof item === 'string'
        ? { id: item, type: BarcodeType.SUPPLY_ITEM }
        : item;
    await handleScanningErrorOrWarning(message, false, barcodeToTrack);
}
const getBatchItemData = async (barcode) => {
    const batchSkuID = await getValueOnce(`${DbRef.BATCH_ITEMS}/${barcode.id}/batchSkuID`);
    if (!batchSkuID) {
        handleScanningError('Invalid batch barcode', barcode);
        return;
    }
    const skuID = batchSkuID;
    const batchSku = await getValueOnce(`${DbRef.BATCH_SKUS}/${batchSkuID}`);
    const { individualSkuID } = batchSku !== null && batchSku !== void 0 ? batchSku : {};
    const sku = await getValueOnce(`${DbRef.SKUS}/${individualSkuID}`);
    const supplyID = sku === null || sku === void 0 ? void 0 : sku.supplyID;
    return {
        skuID,
        supplyID,
    };
};
const getSupplyItemData = async (barcode) => {
    var _a;
    const supplyItem = await getValueOnce(getSupplyItemPath(barcode.id));
    if (!(supplyItem === null || supplyItem === void 0 ? void 0 : supplyItem.skuID)) {
        handleScanningError('Invalid supply barcode', barcode);
        return;
    }
    const { serial } = supplyItem;
    const skuID = (_a = supplyItem.skuID) !== null && _a !== void 0 ? _a : '';
    const sku = await getValueOnce(`${DbRef.SKUS}/${skuID}`);
    if (!(sku === null || sku === void 0 ? void 0 : sku.active)) {
        handleScanningWarning('Inactive supply. Please contact support.', barcode);
        return;
    }
    const { supplyID } = sku;
    return {
        serial,
        skuID,
        supplyID,
    };
};
const barcodeValidation = async (rawBarcodeValue, items) => {
    if (!rawBarcodeValue) {
        return;
    }
    /**
     * Shipping labels may contain the "Group Separator" character, which
     * must be stripped.
     */
    const groupSeparatorChar = '\x1D';
    const barcodeValue = rawBarcodeValue.split(groupSeparatorChar).join('');
    const barcode = await getValueOnce(`${DbRef.BARCODES}/${barcodeValue}`);
    if (!barcode) {
        handleScanningWarning('Invalid barcode', { id: barcodeValue });
        return;
    }
    const isBarcodeAlreadyScanned = items === null || items === void 0 ? void 0 : items.find((item) => item.barcodeValue === barcodeValue);
    if (isBarcodeAlreadyScanned) {
        handleScanningWarning('Barcode already scanned.', barcode);
        return;
    }
    switch (barcode.type) {
        case BarcodeType.BATCH_ITEM: {
            const data = await getBatchItemData(barcode);
            if (!data)
                return;
            return { barcode, barcodeValue, ...data };
        }
        case BarcodeType.SUPPLY_ITEM: {
            const data = await getSupplyItemData(barcode);
            if (!data)
                return;
            return { barcode, barcodeValue, ...data };
        }
        case BarcodeType.GENERIC:
        case BarcodeType.SHIPPING_LABEL: {
            return { barcode, barcodeValue };
        }
    }
};
/**
 * If the item scanned/clicked is not in the list of items to scan, or is
 * already scanned, returns null.
 */
const getUpdatedItemsToScan = (itemsToScan, 
/** Must be passed if scanning */
scannedItem, 
/** Must be passed if using "click" bypass mode (scanning disabled) */
keyBypass) => {
    var _a, _b;
    const indexToUpdate = itemsToScan.findIndex((item) => {
        if (item.isScanned)
            return false;
        if (keyBypass)
            return item.key === keyBypass;
        if (!scannedItem)
            return false;
        const scannedItemHasSupplyID = !!scannedItem.supplyID;
        const matchesSupplyID = (scannedItemHasSupplyID
            && item.supplyID === scannedItem.supplyID);
        /** If `item.serial` is present, serials must match exactly */
        const serialMustMatch = !!item.serial;
        if (serialMustMatch) {
            const matchesSerial = item.serial === scannedItem.serial;
            return matchesSupplyID && matchesSerial;
        }
        const matchesSkuID = item.skuID === scannedItem.skuID;
        return matchesSkuID || matchesSupplyID;
    });
    const currentItem = itemsToScan[indexToUpdate];
    if (!currentItem) {
        handleScanningWarning('Wrong supply', (_a = scannedItem === null || scannedItem === void 0 ? void 0 : scannedItem.barcode) !== null && _a !== void 0 ? _a : {});
        return null;
    }
    let supplyItemID;
    if (((_b = scannedItem === null || scannedItem === void 0 ? void 0 : scannedItem.barcode) === null || _b === void 0 ? void 0 : _b.type) === BarcodeType.SUPPLY_ITEM) {
        supplyItemID = scannedItem.barcode.id;
    }
    else if (keyBypass) {
        // In click mode, assume user is clicking a supplyItem and use the random
        // key as supplyItemID. Include the "scanDisabled" so it is clear that
        // these are fake supplyItemIDs.
        supplyItemID = `scanDisabled-${keyBypass}`;
    }
    const updatedCurrentItem = {
        ...currentItem,
        isScanned: true,
        barcode: scannedItem === null || scannedItem === void 0 ? void 0 : scannedItem.barcode,
        barcodeValue: scannedItem === null || scannedItem === void 0 ? void 0 : scannedItem.barcodeValue,
        supplyItemID,
    };
    // When using bypass, do not change order of items.
    if (keyBypass) {
        // Spread to avoid mutation original array.
        const newItemsToScan = [...itemsToScan];
        newItemsToScan[indexToUpdate] = updatedCurrentItem;
        return newItemsToScan;
    }
    const itemsToScanWithoutCurrentItem = itemsToScan
        .filter((_item, index) => index !== indexToUpdate);
    // Scanned items go to the bottom of the list
    return [...itemsToScanWithoutCurrentItem, updatedCurrentItem];
};
const hasBarcodeType = (item, type) => { var _a; return ((_a = item.barcode) === null || _a === void 0 ? void 0 : _a.type) === type; };

export { barcodeValidation, getUpdatedItemsToScan, handleScanningError, handleScanningWarning, hasBarcodeType };
